import React from 'react';
import {
  Icon as PhosphorIcon,
  ImageIcon,
  FilePdfIcon,
  TableIcon,
  PresentationChartIcon,
  FileTextIcon,
  FilmSlateIcon,
  FileAudioIcon,
  ArchiveIcon,
  FileIcon as PhosphorFileIcon,
} from '@phosphor-icons/react';

interface FileIconProps {
  fileName?: string;
  contentType?: string;
  size?: 'small' | 'medium' | 'large';
  showBackground?: boolean;
}

const getFileIconComponent = (
  fileName?: string,
  contentType?: string,
): React.ElementType<React.ComponentProps<PhosphorIcon>> => {
  const extension = fileName?.split('.').pop()?.toLowerCase();

  // Check by content type first
  if (contentType) {
    if (contentType.startsWith('image/')) return ImageIcon;
    if (contentType === 'application/pdf') return FilePdfIcon;
    if (contentType.includes('spreadsheet') || contentType.includes('excel'))
      return TableIcon;
    if (
      contentType.includes('presentation') ||
      contentType.includes('powerpoint')
    )
      return PresentationChartIcon;
    if (contentType.includes('document') || contentType.includes('word'))
      return FileTextIcon;
    if (contentType.startsWith('video/')) return FilmSlateIcon;
    if (contentType.startsWith('audio/')) return FileAudioIcon;
    if (contentType.startsWith('text/')) return FileTextIcon;
  }

  // Fallback to extension if fileName is available
  if (!fileName) return PhosphorFileIcon;

  switch (extension) {
    case 'pdf':
      return FilePdfIcon;
    case 'doc':
    case 'docx':
      return FileTextIcon;
    case 'xls':
    case 'xlsx':
      return TableIcon;
    case 'ppt':
    case 'pptx':
      return PresentationChartIcon;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'webp':
      return ImageIcon;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
      return FilmSlateIcon;
    case 'mp3':
    case 'wav':
    case 'flac':
      return FileAudioIcon;
    case 'txt':
    case 'md':
      return FileTextIcon;
    case 'zip':
    case 'rar':
    case '7z':
      return ArchiveIcon;
    default:
      return PhosphorFileIcon;
  }
};

const getIconColor = (fileName?: string, contentType?: string): string => {
  const extension = fileName?.split('.').pop()?.toLowerCase();

  if (
    contentType?.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')
  ) {
    return '#4CAF50'; // Green for images
  }
  if (contentType === 'application/pdf' || extension === 'pdf') {
    return '#F44336'; // Red for PDFs
  }
  if (
    contentType?.includes('spreadsheet') ||
    ['xls', 'xlsx'].includes(extension || '')
  ) {
    return '#4CAF50'; // Green for spreadsheets
  }
  if (
    contentType?.includes('presentation') ||
    ['ppt', 'pptx'].includes(extension || '')
  ) {
    return '#FF9800'; // Orange for presentations
  }
  if (
    contentType?.includes('document') ||
    ['doc', 'docx'].includes(extension || '')
  ) {
    return '#2196F3'; // Blue for documents
  }
  if (
    contentType?.startsWith('video/') ||
    ['mp4', 'avi', 'mov'].includes(extension || '')
  ) {
    return '#9C27B0'; // Purple for videos
  }
  if (
    contentType?.startsWith('audio/') ||
    ['mp3', 'wav'].includes(extension || '')
  ) {
    return '#FF5722'; // Deep orange for audio
  }
  return '#757575'; // Gray for unknown types
};

const getSizeProps = (size: 'small' | 'medium' | 'large') => {
  switch (size) {
    case 'small':
      return { iconSize: 16, boxSize: 32 };
    case 'large':
      return { iconSize: 24, boxSize: 64 };
    default:
      return { iconSize: 20, boxSize: 48 };
  }
};

const FileIcon: React.FC<FileIconProps> = ({
  fileName,
  contentType,
  size = 'medium',
  showBackground = true,
}) => {
  const IconComponent = getFileIconComponent(fileName, contentType);
  const iconColor = getIconColor(fileName, contentType);
  const { iconSize, boxSize } = getSizeProps(size);

  if (showBackground) {
    return (
      <div
        style={{
          width: boxSize,
          height: boxSize,
          backgroundColor: `${iconColor}20`,
          border: `1px solid ${iconColor}40`,
        }}
        className="rounded flex items-center justify-center"
      >
        <IconComponent size={iconSize} style={{ color: iconColor }} />
      </div>
    );
  }

  return <IconComponent size={iconSize} style={{ color: iconColor }} />;
};

export default FileIcon;
